/**
 * 影片網址處理工具
 *
 * 提供統一的影片 ID 提取、網址標準化、驗證功能
 * 支援平台：Instagram Reels, Facebook Reels, YouTube (橫式/直式)
 */

/**
 * 提取 Instagram Reel ID
 * 支援格式:
 * - https://www.instagram.com/reel/ID/
 * - https://www.instagram.com/username/reel/ID/
 */
export function extractInstagramReelId(url) {
  const patterns = [
    /instagram\.com\/reel\/([a-zA-Z0-9_-]+)/,
    /instagram\.com\/[^/]+\/reel\/([a-zA-Z0-9_-]+)/
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }

  return null;
}

/**
 * 提取 Facebook Reel ID
 * 支援格式:
 * - https://www.facebook.com/reel/ID
 * - https://facebook.com/username/videos/ID
 */
export function extractFacebookReelId(url) {
  const patterns = [
    /facebook\.com\/reel\/([a-zA-Z0-9_-]+)/,
    /facebook\.com\/[^/]+\/videos\/(\d+)/
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }

  return null;
}

/**
 * 提取 YouTube Shorts ID
 * 支援格式:
 * - https://www.youtube.com/shorts/ID
 * - https://www.youtube.com/watch?v=ID (用戶可能複製到橫式格式)
 * - https://youtu.be/ID (短網址)
 * - 以上格式帶 ?si=xxx 或 ?feature=share 等參數
 */
export function extractYoutubeShortsId(url) {
  const patterns = [
    /youtube\.com\/shorts\/([\w-]+)/,        // shorts/ID
    /youtube\.com\/watch\?v=([\w-]+)/,       // watch?v=ID
    /youtu\.be\/([\w-]+)/                    // youtu.be/ID
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }

  return null;
}

/**
 * 提取 YouTube 影片 ID (橫式)
 * 支援格式:
 * - https://www.youtube.com/watch?v=ID
 * - https://youtu.be/ID
 * - https://www.youtube.com/shorts/ID (用戶可能複製到直式格式)
 * - 以上格式帶 ?si=xxx 或 ?feature=share 等參數
 */
export function extractYoutubeId(url) {
  const patterns = [
    /youtube\.com\/watch\?v=([\w-]+)/,       // watch?v=ID
    /youtu\.be\/([\w-]+)/,                   // youtu.be/ID
    /youtube\.com\/shorts\/([\w-]+)/         // shorts/ID
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }

  return null;
}

/**
 * 標準化影片網址 (短影音)
 * 根據平台提取 ID 並轉換為標準格式
 */
export function normalizeShortVideoUrl(url, platform) {
  let videoId = null;

  switch (platform) {
    case 'instagram-reels':
      videoId = extractInstagramReelId(url);
      return videoId ? `https://www.instagram.com/reel/${videoId}` : url;

    case 'facebook-reels':
      videoId = extractFacebookReelId(url);
      return videoId ? `https://www.facebook.com/reel/${videoId}` : url;

    case 'youtube-shorts':
      videoId = extractYoutubeShortsId(url);
      return videoId ? `https://www.youtube.com/shorts/${videoId}` : url;

    default:
      return url;
  }
}

/**
 * 標準化 YouTube 網址 (橫式影音)
 */
export function normalizeYoutubeUrl(url) {
  const videoId = extractYoutubeId(url);
  return videoId ? `https://www.youtube.com/watch?v=${videoId}` : url;
}

/**
 * 清理網址（去除多餘參數和尾端斜線）
 * 用於短影音（Instagram, Facebook, YouTube Shorts）
 */
export function cleanShortVideoUrl(url) {
  return url.split('?')[0].replace(/\/+$/, '').trim();
}

/**
 * 清理網址（保留必要參數）
 * 用於橫式 YouTube 影片（需保留 ?v= 參數）
 */
export function cleanRegularVideoUrl(url, platform) {
  if (!url) return '';

  const trimmed = url.trim().replace(/\/+$/, '');

  try {
    const parsed = new URL(trimmed);

    // YouTube 長影片處理 query string（只留 v）
    if (platform === 'youtube' && parsed.hostname.includes('youtube.com') && parsed.pathname === '/watch') {
      const v = parsed.searchParams.get('v');
      if (v) return `https://www.youtube.com/watch?v=${v}`;
    }

    // 其他平台或格式：去除 query string
    return `${parsed.origin}${parsed.pathname}`;
  } catch (e) {
    // fallback（無效網址）
    return trimmed;
  }
}

/**
 * 驗證短影音網址
 * @param {string} url - 影片網址
 * @param {string} platform - 平台類型
 * @param {Object} URL_PATTERNS - TikTok 等其他平台的正則（選填）
 * @returns {string|true} 錯誤訊息或 true
 */
export function validateShortVideoUrl(url, platform, URL_PATTERNS = {}) {
  const trimmed = url?.trim();

  if (!trimmed) return '請輸入影音網址';
  if (!platform) return '請先選擇影音來源平台';

  let videoId = null;

  switch (platform) {
    case 'youtube-shorts':
      videoId = extractYoutubeShortsId(trimmed);
      if (!videoId) {
        return '無法識別 YouTube Shorts ID，請確認網址格式 (例: https://www.youtube.com/shorts/xxxxxxxxxxx)';
      }
      break;

    case 'instagram-reels':
      videoId = extractInstagramReelId(trimmed);
      if (!videoId) {
        return '無法識別 Instagram Reel ID，請確認網址格式 (例: https://www.instagram.com/reel/xxx 或 https://www.instagram.com/帳號/reel/xxx)';
      }
      break;

    case 'facebook-reels':
      videoId = extractFacebookReelId(trimmed);
      if (!videoId) {
        return '無法識別 Facebook Reel ID，請確認網址格式 (例: https://www.facebook.com/reel/xxx)';
      }
      break;

    case 'tiktok':
      // TikTok 使用傳統正則驗證（因為沒有提取函數）
      const pattern = URL_PATTERNS[platform];
      if (pattern) {
        const clean = cleanShortVideoUrl(trimmed);
        if (!pattern.test(clean)) {
          return '網址格式錯誤，請輸入： https://www.tiktok.com/@帳號/video/xxx 格式';
        }
      } else {
        return '請先選擇影音來源平台';
      }
      break;

    default:
      return '網址格式錯誤，請確認是否為有效影片連結';
  }

  return true;
}

/**
 * 驗證橫式影音網址
 * @param {string} url - 影片網址
 * @param {string} platform - 平台類型
 * @returns {string|true} 錯誤訊息或 true
 */
export function validateRegularVideoUrl(url, platform) {
  const trimmed = url?.trim();

  if (!trimmed) return '請輸入影音網址';
  if (!platform) return '請先選擇影音來源平台';

  if (platform === 'youtube') {
    const videoId = extractYoutubeId(trimmed);
    if (!videoId) {
      return '無法識別 YouTube 影片 ID，請確認網址格式 (例: https://www.youtube.com/watch?v=xxx 或 https://youtu.be/xxx)';
    }
  }

  return true;
}

/**
 * 取得 placeholder 文字 (短影音)
 */
export function getShortVideoPlaceholder(platform) {
  switch (platform) {
    case 'youtube-shorts':
      return '支援 youtube.com/shorts/xxx、youtube.com/watch?v=xxx、youtu.be/xxx';
    case 'instagram-reels':
      return '支援 instagram.com/reel/xxx 或 instagram.com/帳號/reel/xxx';
    case 'facebook-reels':
      return 'https://www.facebook.com/reel/xxx';
    default:
      return '輸入『衛教短影音』影音網址';
  }
}

/**
 * 取得 placeholder 文字 (橫式影音)
 */
export function getRegularVideoPlaceholder() {
  return '支援 youtube.com/watch?v=xxx、youtu.be/xxx';
}
